import type { Signal } from '@preact/signals-react';
import { useSignal, useSignalEffect } from '@preact/signals-react';
import clsx from 'clsx';
import dot from 'dot-object';
import type React from 'react';
import type { CSSProperties, Ref } from 'react';
import { useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataUpdate,
  OdinGetDataFunction,
  OdinGetSubscriberFunction,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import usePropertyHistory from '../../../common/hooks/use-property-history';
import useResizeObserver from '../../../common/hooks/use-resize-observer';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { TypeOrArray } from '../../common-interfaces';
import type { ContinuousScrollerRef } from '../../continuous-scroller/continuous-scroller.component';
import ContinuousScroller from '../../continuous-scroller/continuous-scroller.component';
import { filterData } from '../../continuous-scroller/continuous-scroller-functions';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
  ScrollerData,
  ScrollerDataLoadInspectorFunction,
  ScrollerItemsChangedFunction,
} from '../../continuous-scroller/continuous-scroller-types';
import AddNewModal from '../../custom-modals/add-new-modal/add-new-modal.component';
import type { DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import { DataSearchLocation } from '../../enums';
import DropDownOption from '../../fields/drop-down/drop-down-option.component';
import type { ActionSectionItem } from '../../fields/drop-down/drop-down-props';
import type { DropDownOptionTemplateType, DropDownResult } from '../../fields/drop-down/drop-down-types';
import { DropDownSelectionType } from '../../fields/drop-down/drop-down-types';
import type { JsonData, JsonDataItem } from '../../generic-types';

export interface DropDownOptionRendererProps extends DynamicFieldDataProps {
  ref?: Ref<DropDownOptionRendererRef>;
  id?: string | number;
  value?: Array<DropDownResult>;
  setDefaultValue: boolean;
  highlightedValue?: Array<DropDownResult>;
  /** Prefix for each option's DOM id, referenced by the list container's aria-activedescendant. */
  optionIdPrefix?: string;
  selectionType?: DropDownSelectionType;
  useCheckboxForMultiSelect?: boolean;
  removeOnSelect?: boolean;
  allowDuplicateCall?: boolean;
  itemsToRemove?: Array<DropDownResult>;
  onChange?: (value: TypeOrArray<DropDownResult>) => void;
  onDoubleClick?: (value: DropDownResult) => void;
  optionSelectedStyle?: string;
  optionHoverStyle?: string;
  optionHighlightedStyle?: string;
  optionTemplate?: DropDownOptionTemplateType;
  scrollElement?: Element;
  getData: OdinGetDataFunction;
  getSubscriber?: OdinGetSubscriberFunction;
  filterSignal: Signal<Array<ContinuousScrollerFilter>>;
  filterLocation?: DataSearchLocation;
  dataLoadInspector?: ScrollerDataLoadInspectorFunction;
  dataLoadIndicator?: React.JSX.Element;
  itemsChanged?: ScrollerItemsChangedFunction;
  scrollerStyles?: CSSProperties;
  className?: string;
  onNewItemCreated?: (value: JsonDataItem) => void;
  isListOpen?: boolean;
  minimumSearchCharacters?: number;
  showDataOnSearch?: boolean;
  actionItems?: Array<ActionSectionItem>;
}

export interface DropDownOptionRendererRef {
  getDataOverwritePageCache: () => void;
  resetDropDownData: () => void;
  resetDropDownHeight: () => void;
}

const SELECTED_FIELD = '__selected';

const DropDownOptionRenderer = (props: DropDownOptionRendererProps) => {
  const {
    ref,
    id,
    textField,
    valueField,
    archivedField,
    data,
    value,
    setDefaultValue,
    highlightedValue,
    optionIdPrefix,
    selectionType = DropDownSelectionType.Single,
    useCheckboxForMultiSelect = true,
    removeOnSelect = false,
    allowDuplicateCall,
    itemsToRemove,
    onChange,
    onDoubleClick,
    optionSelectedStyle,
    optionHoverStyle,
    optionHighlightedStyle,
    className,
    optionTemplate,
    scrollElement,
    getData,
    getSubscriber,
    filterSignal,
    filterLocation,
    dataLoadInspector,
    dataLoadIndicator,
    itemsChanged,
    scrollerStyles,
    pageSize = 50,
    showAddNewOption = false,
    onNewItemCreated,
    isListOpen,
    showDataOnSearch,
    minimumSearchCharacters,
    actionItems,
  } = props;

  const [addModalVisible, setAddModalVisible] = useState(false);
  const [staticData, setStaticData] = useState<JsonData>();
  const [optionContainerHeight, setOptionContainerHeight] = useState(0);

  const selectedValue = useRef<Array<DropDownResult> | undefined>(undefined);
  const scrollerRef = useRef<ContinuousScrollerRef>(null);

  const uniqueId = useId();
  const tagCreator = useTestTag('drop-down-option');
  const { t } = useTranslation();

  const isMultiSelect = selectionType === DropDownSelectionType.Multiple;
  const isMultiSelectCheckbox = isMultiSelect && useCheckboxForMultiSelect;

  const sortSignal = useSignal<Array<ContinuousScrollerSort>>([]);

  const [optionsContainerRef] = useResizeObserver(handleResize);

  // I hate to add this here, but if I don't I get an error when it's used in the hook below
  const setScrollerItemSelected = useCallback(
    (item: DropDownResult, selectedValue: boolean) => {
      const selectedItem = scrollerRef.current
        ?.getAllData()
        .find((loadedItem) => dot.pick(valueField, loadedItem) === item.value);
      if (selectedItem) {
        selectedItem[SELECTED_FIELD] = selectedValue;
      }
    },
    [valueField]
  );

  usePropertyHistory((prevValue, currValue) => {
    if (isMultiSelectCheckbox && scrollerRef.current && prevValue && currValue) {
      if (currValue.length < prevValue.length) {
        const removedItem = prevValue.find(
          (prevItem) => !currValue.some((currItem) => prevItem.value === currItem.value)
        );

        if (removedItem) {
          setScrollerItemSelected(removedItem, false);
        }
      } else if (currValue.length > prevValue.length) {
        const addedItem = currValue.find(
          (currItem) => !prevValue.some((prevItem) => prevItem.value === currItem.value)
        );

        if (addedItem) {
          setScrollerItemSelected(addedItem, true);
        }
      }
    }

    selectedValue.current = value;
  }, value);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger when data changes, other deps are stable
  useEffect(() => {
    if (Array.isArray(data)) {
      itemsChanged?.(data);
      setStaticData(filterSignal.value.length < 1 ? data : filterData(data, filterSignal.value));

      if (setDefaultValue && !hasSelectedValue(data)) {
        const result: DropDownResult = {
          text: dot.pick(textField, data[0]),
          value: dot.pick(valueField, data[0]),
          archived: archivedField ? !!dot.pick(archivedField, data[0]) : false,
          originalData: data[0],
        };

        onChange?.(result);
      }
    }
  }, [data]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger when isMultiSelectCheckbox changes, sortSignal and other deps are stable
  useEffect(() => {
    if (isMultiSelectCheckbox && sortSignal.value.length === 0) {
      // only add the sort if its hasn't already been added
      sortSignal.value.push(
        {
          scrollerId: id,
          field: SELECTED_FIELD,
          sortDirection: 'descending',
        },
        {
          scrollerId: id,
          field: textField,
          sortDirection: 'ascending',
          caseSensitive: false,
        }
      );
    }
  }, [isMultiSelectCheckbox]);

  useEffect(() => {
    if (!isListOpen) {
      // time out equal to the Overlay Panel's transition duration so that users don't see the list sorting while they
      // close the dropdown.
      setTimeout(() => scrollerRef.current?.sortLocalData(), 400);
    }
  }, [isListOpen]);

  useSignalEffect(() => {
    if (Array.isArray(data) && filterSignal.value.length > 0) {
      setStaticData(filterData(data, filterSignal.value));
    }
  });

  function handleResize({ height }: { height?: number }) {
    if (height !== undefined && height > optionContainerHeight) {
      const maxHeight = parseInt(String(scrollerStyles?.maxHeight ?? '200'), 10);
      if (height < maxHeight) {
        setOptionContainerHeight(height);
      } else {
        setOptionContainerHeight(maxHeight);
      }
    }
  }

  const modifyScrollerData = (_page: number, data: JsonData) => {
    let remainingItems = selectedValue.current ? [...selectedValue.current] : [];
    // speed is more important than order
    for (let i = data.length; i-- !== 0; ) {
      const item = data[i];
      const itemValue = dot.pick(valueField, item);
      const isSelected =
        selectedValue.current !== undefined &&
        selectedValue.current.findIndex((selectedItem) => selectedItem.value === itemValue) > -1;

      if (isSelected) {
        remainingItems = remainingItems.filter((selectedItem) => selectedItem.value !== itemValue);
      }

      data[i] = {
        ...item,
        [SELECTED_FIELD]: isSelected,
      };
    }

    for (let j = remainingItems.length; j-- !== 0; ) {
      const remainingItem = remainingItems[j];

      data.push({
        [valueField]: remainingItem.value,
        [textField]: remainingItem.text,
        [archivedField || 'archived']: remainingItem.archived || false,
        [SELECTED_FIELD]: true,
      });
    }

    setTimeout(() => {
      scrollerRef.current?.sortLocalData();
    });
  };

  const handleOnChange = useCallback(
    (item: DropDownResult) => {
      if (isMultiSelectCheckbox && scrollerRef.current) {
        const isCurrentlySelected =
          selectedValue.current !== undefined &&
          selectedValue.current.findIndex((currentItem) => currentItem.value === item.value) > -1;
        setScrollerItemSelected(item, !isCurrentlySelected);
      }

      onChange?.(item);
    },
    [isMultiSelectCheckbox, setScrollerItemSelected, onChange]
  );

  const hasSelectedValue = (data: JsonData) => {
    if (!selectedValue.current) {
      return false;
    }

    return (
      data.findIndex((item) => {
        const itemValue = dot.pick(valueField, item);
        return selectedValue.current && selectedValue.current.findIndex((value) => value.value === itemValue) > -1;
      }) > -1
    );
  };

  const dataRetriever = useMemo<(OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper>) | undefined>(() => {
    if (data && !Array.isArray(data)) {
      if (data.getSubscriber) {
        return {
          getData,
          getSubscriber,
          saveData: data.saveData,
        };
      } else {
        return {
          getData,
          saveData: data.saveData,
        };
      }
    }
  }, [getData, getSubscriber, data]);

  const onAddNewClicked = useCallback(() => setAddModalVisible(true), []);

  const onAddModalHidden = useCallback(() => setAddModalVisible(false), []);

  /**
   * This method works in reverse of how one would think; we are trying to kick out as soon as possible. Only if the
   * drop-down is a multi-select and remove on select is true and there are currently selected values do we want to run
   * the check to see if we should remove the option. All other scenario's will allow every option to be rendered.
   * @param {JsonDataItem} item - The data item to search for the value.
   * @return {boolean} Returns true if the item was not in the selected values.
   */
  const shouldRenderOption = (item: JsonDataItem): boolean => {
    const removeOnSelectCheck =
      selectedValue.current === undefined ||
      !isMultiSelect ||
      !removeOnSelect ||
      selectedValue.current.findIndex((value) => value.value === dot.pick(valueField, item)) === -1;

    const itemsToRemoveCheck =
      itemsToRemove === undefined ||
      itemsToRemove.findIndex((value) => value.value === dot.pick(valueField, item)) === -1;

    return itemsToRemoveCheck && removeOnSelectCheck;
  };

  const getDataOverwritePageCache = () => {
    scrollerRef.current?.getDataOverwritePageCache();
  };

  const resetDropDownData = () => {
    scrollerRef.current?.__dangerouslyResetScrollerData();
  };

  const resetDropDownHeight = () => {
    setOptionContainerHeight(0);
  };

  useImperativeHandle(ref, () => ({
    getDataOverwritePageCache,
    resetDropDownData,
    resetDropDownHeight,
  }));

  const renderDropDownOptions = (data: ScrollerData, currentItemCount = 0, renderPage = 1) => {
    let lastIndex = 0;

    if (data.length === 0 && renderPage === 1) {
      if (showAddNewOption) {
        return [
          <div
            key={`${valueField}_${currentItemCount + lastIndex - 1}`}
            className="flex cursor-pointer px-2 py-1 font-bold text-foreground hover:bg-muted-foreground hover:text-background"
            onClick={onAddNewClicked}
            ref={tagCreator('add-new')}
          >
            Add New
          </div>,
        ];
      } else {
        // usually this will be called when a search yields no results, but it could happen when a drop-down has no data
        // at all
        return <p className="text-center">{t('no-data-returned')}</p>;
      }
    } else {
      const dropDownOptions = data.map((item, index) => {
        lastIndex = index;

        if (shouldRenderOption(item)) {
          const uniqueKey = `${uniqueId}_${item[valueField] ?? item.__uniqueId ?? currentItemCount + index}`;
          if (showDataOnSearch) {
            if (
              !minimumSearchCharacters ||
              (filterSignal &&
                filterSignal.value.length > 0 &&
                (filterSignal.value[0].value as string).length > Number(minimumSearchCharacters))
            ) {
              return (
                <DropDownOption
                  key={uniqueKey}
                  textField={textField}
                  valueField={valueField}
                  archivedField={archivedField}
                  data={item}
                  selectionType={selectionType}
                  selectedValue={value}
                  highlightedValue={highlightedValue}
                  optionIdPrefix={optionIdPrefix}
                  useCheckboxForMultiSelect={useCheckboxForMultiSelect}
                  onChange={handleOnChange}
                  onItemDoubleClicked={onDoubleClick}
                  hoverStyle={optionHoverStyle}
                  selectedStyle={optionSelectedStyle}
                  highlightedStyle={optionHighlightedStyle}
                  optionTemplate={optionTemplate}
                />
              );
            } else {
              return null;
            }
          } else {
            return (
              <DropDownOption
                key={uniqueKey}
                textField={textField}
                valueField={valueField}
                archivedField={archivedField}
                data={item}
                selectionType={selectionType}
                selectedValue={value}
                highlightedValue={highlightedValue}
                optionIdPrefix={optionIdPrefix}
                useCheckboxForMultiSelect={useCheckboxForMultiSelect}
                onChange={handleOnChange}
                onItemDoubleClicked={onDoubleClick}
                hoverStyle={optionHoverStyle}
                selectedStyle={optionSelectedStyle}
                highlightedStyle={optionHighlightedStyle}
                optionTemplate={optionTemplate}
              />
            );
          }
        }

        return null;
      });

      if (showAddNewOption && renderPage === 1) {
        dropDownOptions.unshift(
          <div
            key={`${valueField}_${currentItemCount + lastIndex + 1}`}
            className="flex cursor-pointer px-2 py-1 font-bold text-foreground hover:bg-muted-foreground hover:text-background"
            onClick={onAddNewClicked}
            ref={tagCreator('add-new')}
          >
            Add New
          </div>
        );
      }

      return dropDownOptions;
    }
  };

  const optionListFinalStyle = clsx('py-1 focus:outline-hidden', className);

  const renderContinuousScroller = () => {
    return (
      dataRetriever && (
        <ContinuousScroller
          ref={scrollerRef}
          dataRetriever={dataRetriever}
          scrollingElement={scrollElement}
          filterSignal={filterSignal}
          sortSignal={sortSignal}
          searchLocation={filterLocation}
          sortLocation={DataSearchLocation.Local}
          canMakeDuplicateCall={allowDuplicateCall}
          canUpdateData={true}
          idFieldName={valueField}
          dataLoadInspector={dataLoadInspector}
          dataLoadIndicator={dataLoadIndicator}
          modifyData={isMultiSelectCheckbox ? modifyScrollerData : undefined}
          itemsChanged={itemsChanged}
          scrollerStyles={{ ...scrollerStyles, minHeight: optionContainerHeight }}
          className={optionListFinalStyle}
          pageSize={pageSize}
          archivedField={archivedField}
          actionItems={actionItems}
        >
          {(data, currentItemCount, page) => (
            <div ref={optionsContainerRef}>{renderDropDownOptions(data, currentItemCount, page)}</div>
          )}
        </ContinuousScroller>
      )
    );
  };

  const optionListStaticFinalStyle = clsx('custom-scroll overflow-y-auto py-1', className);

  const renderStaticData = () => (
    <div className={optionListStaticFinalStyle} style={{ ...scrollerStyles, minHeight: optionContainerHeight }}>
      {staticData && renderDropDownOptions(staticData, staticData.length, 1)}
    </div>
  );

  const dropDownRendererOnItemCreated = (value: JsonDataItem) => {
    if (onNewItemCreated) {
      onNewItemCreated(value);
      getDataOverwritePageCache();
    }
  };

  return (
    <>
      {scrollElement && !Array.isArray(data) ? (
        renderContinuousScroller()
      ) : (
        <div ref={optionsContainerRef}>{renderStaticData()}</div>
      )}
      {showAddNewOption && (
        <AddNewModal
          fieldId={id || 0}
          header={t('add-new-item')}
          textField={textField}
          valueField={valueField}
          data={dataRetriever}
          visible={addModalVisible}
          onClose={onAddModalHidden}
          // drop-downs and list boxes will only have a single filter
          defaultValue={filterSignal.value[0]?.value as string}
          onItemCreated={dropDownRendererOnItemCreated}
        />
      )}
    </>
  );
};

export default DropDownOptionRenderer;
